// Multigudang (mig 7200) — helper MURNI (tanpa I/O), dipakai route tarif,
// katalog reseller, dan layar admin/pembeli.
//
// ── EMPAT ATURAN YANG MENGIKAT MODUL INI ───────────────────────────────────
//
//  1. **GUDANG UTAMA = LOKASI TOKO YANG SUDAH ADA.** Ia dikenali dengan kunci
//     `null` di mana pun (produk tanpa gudang, kelompok barang, rincian
//     kutipan). Barisnya di `warehouses` (`is_main`) hanya menyimpan nama,
//     alamat, dan daftar ekspedisi — lokasinya tetap `site_settings.
//     shipping_origin_*`. Baris utama yang belum ada dibaca sebagai bawaan.
//
//  2. **EKSPEDISI GUDANG = HIMPUNAN BAGIAN EKSPEDISI AKTIF TOKO.** `null` =
//     semua (perilaku sebelum Multigudang, tanpa saringan apa pun). Bukan
//     daftar kurir kedua — mig 3980 sengaja menghapus daftar ganda.
//
//  3. **TARIF GABUNGAN = IRISAN, BUKAN GABUNGAN.** Pembeli memilih SATU
//     ekspedisi; yang ditawarkan hanya layanan yang ada di SEMUA gudang yang
//     terlibat, dengan harga = jumlah tarif tiap gudang dan estimasi = yang
//     terlama. Tak ada irisan = daftar kosong → jalur cadangan yang sudah ada
//     (ongkir ditetapkan admin).
//
//  4. **NOMINAL TETAP MILIK SERVER.** `legs` di sini hanya bahan tampilan &
//     bahan kutipan; yang ditagih tetap baris `shipping_quotes` yang
//     diverifikasi ulang `consume_shipping_quote` per gudang.

import { rateKey, type RateLeg, type ShippingRate } from "@/lib/biteship";
import { matchActiveExpedition } from "@/lib/cekOngkir";
import type { ManualRate } from "@/lib/expeditionTariff";
import { normalizeCourierCode, type Expedition } from "@/lib/expeditions";

/** Kunci `site_settings` saklar Multigudang (publik, bawaan MATI). */
export const MULTI_WAREHOUSE_SETTING_KEY = "multi_warehouse_enabled";

/**
 * Nilai mentah `site_settings` → saklar. Bawaan MATI: hanya "true/1/on/yes"
 * yang menyala — kembaran SQL `multi_warehouse_enabled(store)` (mig 7200),
 * BUKAN `flagOn` (bawaan-nyala, teks asing = ON). Dua pembaca yang tak
 * sepakat tentang teks asing = route & gerbang kutipan berbeda pendapat.
 */
export function parseMultiWarehouseFlag(raw: unknown): boolean {
  return ["true", "1", "on", "yes"].includes(String(raw ?? "").trim().toLowerCase());
}
export const DEFAULT_MAIN_WAREHOUSE_NAME = "Gudang Utama";
/** Batas wajar barang per permintaan tarif — Request Kirim terbesar jauh di bawahnya. */
export const MAX_RATE_ITEMS = 300;

// ─── 1. Data gudang ─────────────────────────────────────────────────────────

/** Satu baris `warehouses`, sudah dikoersi. */
export type WarehouseRecord = {
  id: string;
  name: string;
  isMain: boolean;
  areaId: string | null;
  districtKey: string | null;
  locationLabel: string;
  address: string;
  countsStock: boolean;
  /** `null` = semua ekspedisi aktif toko. */
  expeditionIds: string[] | null;
  sortOrder: number;
  archivedAt: string | null;
};

/**
 * Gudang sebagaimana dipakai layar & route. `key` `null` = Gudang Utama —
 * kunci yang sama dengan `products.warehouse_id` NULL dan `legs[].w` null.
 */
export type WarehouseView = {
  key: string | null;
  /** Id baris; `null` bila baris utama belum pernah disimpan. */
  id: string | null;
  name: string;
  isMain: boolean;
  areaId: string | null;
  districtKey: string | null;
  locationLabel: string;
  address: string;
  countsStock: boolean;
  expeditionIds: string[] | null;
};

/**
 * Kolom `warehouses` yang dibaca semua pembaca — satu daftar, satu bentuk.
 * Literal tunggal (bukan rakitan `+`) supaya tipe hasil supabase-js tetap
 * terurai (CLAUDE.md gerbang 3).
 */
export const WAREHOUSE_SELECT =
  "id, name, is_main, area_id, district_key, location_label, address, counts_stock, expedition_ids, sort_order, archived_at";

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** Baris mentah (PostgREST) → `WarehouseRecord`, atau `null` bila tak sah. */
export function mapWarehouseRow(row: unknown): WarehouseRecord | null {
  if (!row || typeof row !== "object") return null;
  const r = row as Record<string, unknown>;
  const id = str(r.id).trim();
  const name = str(r.name).trim();
  if (id === "" || name === "") return null;
  const ids = Array.isArray(r.expedition_ids)
    ? (r.expedition_ids as unknown[]).filter((x): x is string => typeof x === "string" && x !== "")
    : null;
  return {
    id,
    name,
    isMain: r.is_main === true,
    areaId: str(r.area_id).trim() || null,
    districtKey: str(r.district_key).trim() || null,
    locationLabel: str(r.location_label),
    address: str(r.address),
    countsStock: r.counts_stock !== false,
    expeditionIds: ids && ids.length > 0 ? ids : null,
    sortOrder: Number.isFinite(Number(r.sort_order)) ? Number(r.sort_order) : 0,
    archivedAt: str(r.archived_at) || null,
  };
}

function toView(rec: WarehouseRecord): WarehouseView {
  return {
    key: rec.isMain ? null : rec.id,
    id: rec.id,
    name: rec.name,
    isMain: rec.isMain,
    areaId: rec.isMain ? null : rec.areaId,
    districtKey: rec.isMain ? null : rec.districtKey,
    locationLabel: rec.locationLabel,
    address: rec.address,
    countsStock: rec.countsStock,
    expeditionIds: rec.expeditionIds,
  };
}

/** Gudang Utama bawaan — dipakai selama barisnya belum pernah disimpan. */
export function defaultMainWarehouse(): WarehouseView {
  return {
    key: null,
    id: null,
    name: DEFAULT_MAIN_WAREHOUSE_NAME,
    isMain: true,
    areaId: null,
    districtKey: null,
    locationLabel: "",
    address: "",
    countsStock: true,
    expeditionIds: null,
  };
}

export type WarehouseDirectory = {
  main: WarehouseView;
  /** Cabang AKTIF (belum diarsip), urut `sortOrder` lalu nama. */
  branches: WarehouseView[];
};

export function resolveWarehouses(rows: readonly WarehouseRecord[]): WarehouseDirectory {
  const mainRow = rows.find((r) => r.isMain);
  const branches = rows
    .filter((r) => !r.isMain && r.archivedAt === null)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "id"))
    .map(toView);
  return { main: mainRow ? toView(mainRow) : defaultMainWarehouse(), branches };
}

/** Semua gudang aktif, utama lebih dulu. */
export function listWarehouses(dir: WarehouseDirectory): WarehouseView[] {
  return [dir.main, ...dir.branches];
}

/**
 * Gudang untuk satu kunci (`products.warehouse_id`). NULL, id baris utama,
 * atau id yang tak (lagi) dikenal → `null` untuk yang terakhir; dua yang
 * pertama → Gudang Utama.
 */
export function findWarehouse(
  dir: WarehouseDirectory,
  warehouseId: string | null | undefined
): WarehouseView | null {
  const id = (warehouseId ?? "").trim();
  if (id === "" || (dir.main.id !== null && id === dir.main.id)) return dir.main;
  return dir.branches.find((w) => w.id === id) ?? null;
}

/** Nama gudang untuk ditampilkan; id tak dikenal → "Gudang lain". */
export function warehouseName(
  dir: WarehouseDirectory,
  warehouseId: string | null | undefined
): string {
  return findWarehouse(dir, warehouseId)?.name ?? "Gudang lain";
}

// ─── 2. Kalimat asal pengiriman ─────────────────────────────────────────────

/** "A" · "A dan B" · "A, B, dan C" — nama ganda/kosong dibuang, urutan dijaga. */
export function joinIndonesianList(names: readonly string[]): string {
  const seen = new Set<string>();
  const list: string[] = [];
  for (const raw of names) {
    const n = raw.trim();
    if (n === "" || seen.has(n.toLowerCase())) continue;
    seen.add(n.toLowerCase());
    list.push(n);
  }
  if (list.length === 0) return "";
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} dan ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, dan ${list[list.length - 1]}`;
}

/** "Dikirim dari Gudang Utama dan Gudang Cabang A". Kosong bila tak ada nama. */
export function originSummaryText(names: readonly string[]): string {
  const joined = joinIndonesianList(names);
  return joined ? `Dikirim dari ${joined}` : "";
}

/**
 * Versi NETRAL untuk katalog reseller white-label: nama gudang toko induk tak
 * boleh tampil di sana. Satu lokasi tak perlu disebut sama sekali.
 */
export function neutralOriginSummaryText(count: number): string {
  return count >= 2 ? `Dikirim dari ${count} lokasi` : "";
}

// ─── 3. Saringan per gudang ─────────────────────────────────────────────────

/**
 * Ekspedisi yang diizinkan gudang ini. `null` = tanpa saringan (semua aktif),
 * dan pemanggil WAJIB memperlakukannya sebagai "tak disaring", bukan "kosong".
 */
export function allowedExpeditionsFor(
  activeExpeditions: readonly Expedition[],
  expeditionIds: readonly string[] | null
): Expedition[] | null {
  if (expeditionIds === null) return null;
  const ids = new Set(expeditionIds);
  return activeExpeditions.filter((e) => e.isActive && ids.has(e.id));
}

/** Kode kurir yang ditanyakan untuk gudang ini (unik & terurut — masuk kunci cache). */
export function courierCodesForWarehouse(
  activeCourierCodes: readonly string[],
  allowed: readonly Expedition[] | null
): string[] {
  if (allowed === null) return [...activeCourierCodes];
  const active = new Set(activeCourierCodes.map((c) => c.trim().toLowerCase()));
  const codes = new Set<string>();
  for (const e of allowed) {
    const code = normalizeCourierCode(e.biteshipCourierCode);
    if (code !== null && active.has(code)) codes.add(code);
  }
  return Array.from(codes).sort();
}

/**
 * Tarif kurir yang memang ditawarkan gudang ini. Pencocokannya
 * `matchActiveExpedition` (persis layanan, lalu baris umum kurirnya) — aturan
 * yang sama dengan layar Cek Ongkir, jadi "JNE REG" yang diizinkan tak
 * meloloskan JNE YES.
 */
export function filterRatesForWarehouse(
  rates: readonly ShippingRate[],
  allowed: readonly Expedition[] | null
): ShippingRate[] {
  if (allowed === null) return [...rates];
  return rates.filter((r) => matchActiveExpedition(allowed, r).kind !== "none");
}

export function filterManualRatesForWarehouse(
  manualRates: readonly ManualRate[],
  allowed: readonly Expedition[] | null
): ManualRate[] {
  if (allowed === null) return [...manualRates];
  const ids = new Set(allowed.map((e) => e.id));
  return manualRates.filter((m) => ids.has(m.expeditionId));
}

// ─── 4. Gabung tarif beberapa gudang ────────────────────────────────────────

/** Tarif satu gudang, siap digabung. */
export type ParcelRates = {
  warehouseId: string | null;
  name: string;
  weightGram: number;
  rates: readonly ShippingRate[];
  manualRates: readonly ManualRate[];
};

/**
 * Lama kirim terbesar (hari) dari teks Biteship ("1 - 2 days", "6 - 8 hours",
 * "2 hari"). Jam dihitung sebagai pecahan hari. `null` = tak terbaca.
 */
export function durationMaxDays(raw: string | null | undefined): number | null {
  const text = (raw ?? "").trim().toLowerCase();
  if (text === "") return null;
  const nums = text.match(/\d+(?:[.,]\d+)?/g);
  if (!nums) return null;
  const max = Math.max(...nums.map((n) => Number(n.replace(",", "."))));
  if (!Number.isFinite(max)) return null;
  return /hour|jam/.test(text) ? max / 24 : max;
}

/**
 * Estimasi gabungan = yang TERLAMA. Paket dari dua gudang baru lengkap saat
 * paket terlambat tiba; menyebut yang tercepat menjanjikan yang tak terjadi.
 * Tak satu pun terbaca → teks pertama yang tak kosong.
 */
export function slowestDuration(durations: readonly string[]): string {
  let best = "";
  let bestDays = -1;
  for (const d of durations) {
    const days = durationMaxDays(d);
    if (days !== null && days > bestDays) {
      best = d;
      bestDays = days;
    }
  }
  if (bestDays >= 0) return best;
  return durations.find((d) => d.trim() !== "") ?? "";
}

function legsOf(parcels: readonly ParcelRates[], prices: readonly number[]): RateLeg[] {
  return parcels.map((p, i) => ({
    warehouseId: p.warehouseId,
    name: p.name,
    weightGram: p.weightGram,
    price: prices[i],
  }));
}

function cheapestBy<T extends { price: number }>(
  items: readonly T[],
  keyOf: (item: T) => string
): Map<string, T> {
  const out = new Map<string, T>();
  for (const item of items) {
    const k = keyOf(item);
    const prev = out.get(k);
    if (!prev || item.price < prev.price) out.set(k, item);
  }
  return out;
}

/**
 * Tarif per gudang → daftar yang bisa dipilih pembeli.
 *
 * Satu gudang: tarifnya apa adanya (hanya ditambah `legs` satu baris supaya
 * kutipannya membawa gudang). ≥2 gudang: IRISAN per layanan kurir
 * (`rateKey`) dan per ekspedisi manual (`expeditionId`), harga dijumlah,
 * estimasi terlama. `quoteId` lama selalu dibuang — kutipan diterbitkan route
 * untuk tarif GABUNGAN, bukan untuk potongannya.
 */
export function combineParcelRates(parcels: readonly ParcelRates[]): {
  rates: ShippingRate[];
  manualRates: ManualRate[];
} {
  if (parcels.length === 0) return { rates: [], manualRates: [] };

  const courierMaps = parcels.map((p) => cheapestBy(p.rates, (r) => rateKey(r)));
  const rates: ShippingRate[] = [];
  for (const [key, base] of courierMaps[0]) {
    const parts = courierMaps.map((m) => m.get(key));
    if (parts.some((x) => x === undefined)) continue;
    const found = parts as ShippingRate[];
    const prices = found.map((x) => x.price);
    const { quoteId: _ignored, legs: _legs, ...rest } = base;
    void _ignored;
    void _legs;
    rates.push({
      ...rest,
      price: prices.reduce((s, n) => s + n, 0),
      duration: parcels.length === 1 ? base.duration : slowestDuration(found.map((x) => x.duration)),
      legs: legsOf(parcels, prices),
    });
  }
  rates.sort(
    (a, b) =>
      a.price - b.price ||
      a.courierCode.localeCompare(b.courierCode) ||
      a.serviceCode.localeCompare(b.serviceCode)
  );

  const manualMaps = parcels.map((p) => cheapestBy(p.manualRates, (m) => m.expeditionId));
  const manualRates: ManualRate[] = [];
  for (const [expeditionId, base] of manualMaps[0]) {
    const parts = manualMaps.map((m) => m.get(expeditionId));
    if (parts.some((x) => x === undefined)) continue;
    const found = parts as ManualRate[];
    const prices = found.map((x) => x.price);
    const { quoteId: _ignored, legs: _legs, ...rest } = base;
    void _ignored;
    void _legs;
    if (parcels.length === 1) {
      manualRates.push({ ...rest, legs: legsOf(parcels, prices) });
      continue;
    }
    const total = parcels.reduce((s, p) => s + p.weightGram, 0);
    manualRates.push({
      ...rest,
      // Kunci tarif (`manualRateKey`) memuat zona & tangga — gabungan
      // menjahit semuanya supaya kuncinya tetap unik dan stabil.
      zoneId: found.map((x) => x.zoneId).join("+"),
      tierId: found.map((x) => x.tierId).join("+"),
      zoneLabel: joinIndonesianList(found.map((x) => x.zoneLabel)),
      minWeightGram: total,
      maxWeightGram: total,
      price: prices.reduce((s, n) => s + n, 0),
      legs: legsOf(parcels, prices),
    });
  }
  manualRates.sort(
    (a, b) => a.price - b.price || a.expeditionName.localeCompare(b.expeditionName, "id")
  );

  return { rates, manualRates };
}

/** Tarif gabungan (≥2 gudang)? Tarif satu gudang tetap membawa `legs` satu baris. */
export function isMultiLegRate(rate: { legs?: readonly RateLeg[] }): boolean {
  return Array.isArray(rate.legs) && rate.legs.length > 1;
}

/** "Gudang Utama Rp 18.000 + Gudang Cabang A Rp 10.000" — rincian untuk admin. */
export function legsBreakdownText(legs: readonly RateLeg[] | undefined): string {
  if (!legs || legs.length < 2) return "";
  return legs
    .map((l) => `${l.name} Rp${Math.round(l.price).toLocaleString("id-ID")}`)
    .join(" + ");
}

// ─── 5. Rincian kutipan (`shipping_quotes.legs`) ────────────────────────────

/** Bentuk yang disimpan di `shipping_quotes.legs` — kunci pendek, dibaca SQL. */
export type QuoteLegRow = { w: string | null; n: string; g: number; a: number };

export function quoteLegsFor(legs: readonly RateLeg[] | undefined): QuoteLegRow[] | null {
  if (!legs || legs.length === 0) return null;
  return legs.map((l) => ({
    w: l.warehouseId,
    n: l.name,
    g: Math.max(0, Math.round(l.weightGram)),
    a: Math.max(0, Math.round(l.price)),
  }));
}

/** `shipping_quotes.legs` mentah → rincian, untuk layar admin. Bentuk tak sah → []. */
export function parseQuoteLegs(raw: unknown): RateLeg[] {
  if (!Array.isArray(raw)) return [];
  const out: RateLeg[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    const g = Number(r.g);
    const a = Number(r.a);
    if (!Number.isFinite(g) || !Number.isFinite(a)) continue;
    out.push({
      warehouseId: typeof r.w === "string" && r.w !== "" ? r.w : null,
      name: str(r.n).trim() || DEFAULT_MAIN_WAREHOUSE_NAME,
      weightGram: g,
      price: a,
    });
  }
  return out;
}

// ─── 6. Barang yang dikirim → permintaan tarif ──────────────────────────────

export type RateItemRef = { itemId: string; qty: number };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Barang dari body permintaan → bentuk `p_items` RPC
 * `shipment_warehouse_parcels`. Id bukan UUID dibuang (RPC akan melempar pada
 * cast), qty dijadikan bilangan bulat ≥ 0 (0 = qty baris). `null` = tak ada
 * barang yang sah / terlalu banyak → pemanggil memakai jalur satu asal.
 */
export function sanitizeRateItems(raw: unknown): { id: string; qty: number }[] | null {
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > MAX_RATE_ITEMS) return null;
  // Id yang sama boleh datang lebih dari sekali (Gabung Tagihan memecah satu
  // baris jadi porsi lunas + belum lunas) → DIJUMLAH di sini, cermin SQL
  // `shipment_warehouse_parcels`. Satu porsi tanpa qty = pakai qty baris (0).
  const byId = new Map<string, number>();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    const id = typeof r.itemId === "string" ? r.itemId.trim().toLowerCase() : "";
    if (!UUID_RE.test(id)) continue;
    const qtyRaw = Math.trunc(Number(r.qty));
    const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 0;
    const prev = byId.get(id);
    byId.set(id, prev === undefined ? qty : prev === 0 || qty === 0 ? 0 : prev + qty);
  }
  const out = [...byId.entries()].map(([id, qty]) => ({ id, qty }));
  return out.length > 0 ? out : null;
}

/** Batas gudang per permintaan tarif — tiap gudang = satu kutipan Biteship. */
export const MAX_RATE_PARCELS = 10;

/** Baris keranjang katalog untuk tarif: produk + berat baris (satuan × pcs). */
export type CatalogRateLine = { productId: string; weightGram: number | null };

/** Body katalog → baris sah. `null` = tak ada / terlalu banyak → jalur satu asal. */
export function sanitizeCatalogLines(raw: unknown): CatalogRateLine[] | null {
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > MAX_RATE_ITEMS) return null;
  const out: CatalogRateLine[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    const productId = typeof r.productId === "string" ? r.productId.trim() : "";
    if (!UUID_RE.test(productId)) continue;
    const w = Number(r.weightGram);
    out.push({
      productId,
      weightGram: r.weightGram === null || !Number.isFinite(w) || w <= 0 ? null : w,
    });
  }
  return out.length > 0 ? out : null;
}

/**
 * Baris katalog → kelompok per gudang. Produk tanpa gudang / gudang utama /
 * tak dikenal → utama (`null`). Sejak mig 7280 klien mengirim berat baris yang
 * SUDAH memuat 500 g/pcs (`catalogCartLineRateWeight`); baris tanpa berat hanya
 * dari klien lama, dan membuat kelompoknya tak berberat (`null`) → pemanggil
 * memakai 1 kg untuk kelompok itu (aturan keranjang lama).
 */
export function groupCatalogLines(
  lines: readonly CatalogRateLine[],
  productWarehouse: ReadonlyMap<string, string | null>,
  dir: WarehouseDirectory
): WarehouseParcel[] {
  const groups = new Map<string | null, WarehouseParcel>();
  for (const line of lines) {
    const raw = productWarehouse.get(line.productId) ?? null;
    const key = raw === null || (dir.main.id !== null && raw === dir.main.id) ? null : raw;
    const g = groups.get(key) ?? { warehouseId: key, weightGram: 0, itemCount: 0 };
    g.itemCount += 1;
    g.weightGram =
      g.weightGram === null || line.weightGram === null ? null : g.weightGram + line.weightGram;
    groups.set(key, g);
  }
  return mapParcelRows(
    [...groups.values()].map((g) => ({
      warehouse_id: g.warehouseId,
      weight_gram: g.weightGram,
      item_count: g.itemCount,
    }))
  );
}

/**
 * Baris nota → barang untuk kutipan: baris produk AKTIF yang masih menunjuk
 * `order_items` (baris yatim tak bisa ditelusuri gudangnya), qty = pcs yang
 * dinotakan. Satu aturan untuk semua layar nota (Ubah Alamat, Ganti ekspedisi).
 */
export function rateItemsFromInvoiceLines(
  lines: ReadonlyArray<{
    kind: string;
    cancelledAt?: string | null;
    orderItemId?: string | null;
    qty?: number | null;
  }>
): RateItemRef[] {
  return lines
    .filter((l) => l.kind === "product_item" && !l.cancelledAt && l.orderItemId)
    .map((l) => ({
      itemId: l.orderItemId as string,
      qty: Math.max(0, Math.trunc(Number(l.qty ?? 0))),
    }));
}

/** Kunci stabil isi daftar barang — array baru tiap render tak boleh memicu fetch ulang. */
export function rateItemsKey(items: readonly RateItemRef[] | undefined): string {
  if (!items || items.length === 0) return "";
  return items
    .map((i) => `${i.itemId}:${Math.max(0, Math.trunc(Number(i.qty) || 0))}`)
    .sort()
    .join(",");
}

/** Satu kelompok barang per gudang (keluaran RPC `shipment_warehouse_parcels`). */
export type WarehouseParcel = {
  warehouseId: string | null;
  /**
   * BERAT TARIF gudang ini (mig 7280: varian tanpa berat = 500 g/pcs).
   * `null` hanya dari DB pra-7280 / baris rusak = berat tak terbaca.
   */
  weightGram: number | null;
  itemCount: number;
};

export function mapParcelRows(rows: unknown): WarehouseParcel[] {
  if (!Array.isArray(rows)) return [];
  const out: WarehouseParcel[] = [];
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const w = Number(r.weight_gram);
    out.push({
      warehouseId: typeof r.warehouse_id === "string" && r.warehouse_id !== "" ? r.warehouse_id : null,
      weightGram: r.weight_gram === null || r.weight_gram === undefined || !Number.isFinite(w) ? null : w,
      itemCount: Math.max(0, Math.trunc(Number(r.item_count) || 0)),
    });
  }
  // Utama lebih dulu, lalu id — urutan stabil untuk kutipan & tampilan.
  out.sort((a, b) =>
    a.warehouseId === b.warehouseId
      ? 0
      : a.warehouseId === null
        ? -1
        : b.warehouseId === null
          ? 1
          : a.warehouseId.localeCompare(b.warehouseId)
  );
  return out;
}

/**
 * Berat tiap kelompok untuk dikutip. Sejak mig 7280 `shipment_warehouse_parcels`
 * sudah memberi BERAT TARIF (varian tanpa berat = 500 g/pcs), jadi kelompok
 * tanpa angka hanya mungkin dari DB pra-7280 / baris rusak → `null` (tak ada
 * angka — lebih baik "ongkir ditetapkan admin" daripada menebak).
 */
export function parcelWeightsForQuote(parcels: readonly WarehouseParcel[]): number[] | null {
  const weights: number[] = [];
  for (const p of parcels) {
    if (p.weightGram === null || p.weightGram <= 0) return null;
    weights.push(p.weightGram);
  }
  return weights;
}

// ─── 7. Kartu admin (Pengaturan Ekspedisi → Multigudang) ────────────────────

/** Satu baris RPC `admin_list_warehouses` (mig 7200). */
export type AdminWarehouse = {
  /** `null` = Gudang Utama yang barisnya belum pernah disimpan. */
  id: string | null;
  name: string;
  isMain: boolean;
  areaId: string | null;
  districtKey: string | null;
  locationLabel: string;
  address: string;
  countsStock: boolean;
  /** `null` = semua ekspedisi aktif toko. */
  expeditionIds: string[] | null;
  sortOrder: number;
  archivedAt: string | null;
  productCount: number;
  /** Produk aktif ber-varian aktif tanpa berat — tarif per gudang butuh berat. */
  missingWeightCount: number;
};

function count(v: unknown): number {
  const n = Math.trunc(Number(v));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Keluaran RPC → daftar kartu: Gudang Utama SELALU ada & di depan (RPC
 * memulangkannya walau barisnya belum disimpan — jaring kedua di sini), lalu
 * cabang aktif urut `sortOrder`/nama. Yang diarsip dibuang: tak bisa
 * disunting, tak bisa dipilih produk.
 */
export function mapAdminWarehouseRows(rows: unknown): AdminWarehouse[] {
  const list: AdminWarehouse[] = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const isMain = r.is_main === true;
    const id = str(r.id).trim() || null;
    const name = str(r.name).trim() || (isMain ? DEFAULT_MAIN_WAREHOUSE_NAME : "");
    if (name === "" || (!isMain && id === null)) continue;
    const ids = Array.isArray(r.expedition_ids)
      ? (r.expedition_ids as unknown[]).filter((x): x is string => typeof x === "string" && x !== "")
      : null;
    list.push({
      id,
      name,
      isMain,
      areaId: isMain ? null : str(r.area_id).trim() || null,
      districtKey: isMain ? null : str(r.district_key).trim() || null,
      locationLabel: str(r.location_label),
      address: str(r.address),
      countsStock: r.counts_stock !== false,
      expeditionIds: ids && ids.length > 0 ? ids : null,
      sortOrder: Number.isFinite(Number(r.sort_order)) ? Number(r.sort_order) : 0,
      archivedAt: str(r.archived_at) || null,
      productCount: count(r.product_count),
      missingWeightCount: count(r.missing_weight_count),
    });
  }
  const main: AdminWarehouse = list.find((w) => w.isMain) ?? {
    id: null,
    name: DEFAULT_MAIN_WAREHOUSE_NAME,
    isMain: true,
    areaId: null,
    districtKey: null,
    locationLabel: "",
    address: "",
    countsStock: true,
    expeditionIds: null,
    sortOrder: 0,
    archivedAt: null,
    productCount: 0,
    missingWeightCount: 0,
  };
  const branches = list
    .filter((w) => !w.isMain && w.archivedAt === null)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "id"));
  return [main, ...branches];
}

/** Isian form gudang (tambah/ubah). */
export type WarehouseDraft = {
  id: string | null;
  isMain: boolean;
  name: string;
  areaId: string;
  districtKey: string;
  locationLabel: string;
  address: string;
  countsStock: boolean;
  /** `null` = semua ekspedisi aktif (termasuk yang ditambahkan nanti). */
  expeditionIds: string[] | null;
};

export const MAX_WAREHOUSE_NAME = 60;
export const MAX_WAREHOUSE_ADDRESS = 500;

export function emptyWarehouseDraft(): WarehouseDraft {
  return {
    id: null,
    isMain: false,
    name: "",
    areaId: "",
    districtKey: "",
    locationLabel: "",
    address: "",
    countsStock: true,
    expeditionIds: null,
  };
}

export function draftFromAdminWarehouse(w: AdminWarehouse): WarehouseDraft {
  return {
    id: w.id,
    isMain: w.isMain,
    name: w.name,
    areaId: w.areaId ?? "",
    districtKey: w.districtKey ?? "",
    locationLabel: w.locationLabel,
    address: w.address,
    countsStock: w.countsStock,
    expeditionIds: w.expeditionIds ? [...w.expeditionIds] : null,
  };
}

/**
 * Validasi form SEBELUM RPC — kalimatnya SAMA dengan penolakan
 * `admin_save_warehouse`, supaya admin membaca satu bahasa entah yang menolak
 * layar atau server. `null` = sah.
 */
export function validateWarehouseDraft(
  draft: WarehouseDraft,
  existing: readonly AdminWarehouse[]
): string | null {
  const name = draft.name.trim();
  if (name === "") return "Nama gudang wajib diisi";
  if (name.length > MAX_WAREHOUSE_NAME) return `Nama gudang maksimal ${MAX_WAREHOUSE_NAME} karakter`;
  const clash = existing.find(
    (w) =>
      w.archivedAt === null &&
      w.name.trim().toLowerCase() === name.toLowerCase() &&
      // Diri sendiri: cabang dikenali id-nya, utama dikenali `isMain` (barisnya
      // mungkin belum punya id).
      !(draft.isMain ? w.isMain : w.id !== null && w.id === draft.id)
  );
  if (clash) return `Nama gudang "${name}" sudah dipakai gudang lain`;
  if (!draft.isMain && (draft.areaId.trim() === "" || draft.districtKey.trim() === "")) {
    return "Pilih kecamatan lokasi gudang";
  }
  if (draft.address.length > MAX_WAREHOUSE_ADDRESS) {
    return `Alamat gudang maksimal ${MAX_WAREHOUSE_ADDRESS} karakter`;
  }
  if (draft.expeditionIds !== null && draft.expeditionIds.length === 0) {
    return "Pilih minimal satu ekspedisi untuk gudang ini";
  }
  return null;
}

/**
 * Ringkasan ekspedisi satu gudang untuk kartu. `warn` = daftar terpilihnya
 * sudah tak punya satu pun ekspedisi AKTIF — gudang itu tak akan pernah
 * mendapat tarif sampai daftarnya diperbarui.
 */
export function warehouseExpeditionSummary(
  expeditions: readonly Expedition[],
  expeditionIds: readonly string[] | null
): { text: string; warn: boolean } {
  if (expeditionIds === null) return { text: "Semua ekspedisi aktif", warn: false };
  const allowed = allowedExpeditionsFor(expeditions, expeditionIds) ?? [];
  if (allowed.length === 0) {
    return { text: "Ekspedisi terpilih sudah nonaktif semua", warn: true };
  }
  return { text: allowed.map((e) => e.name).join(", "), warn: false };
}

/**
 * Ekspedisi aktif yang tersedia di SEMUA gudang. Kosong padahal ada ≥2 gudang
 * = kiriman yang barangnya dari beberapa gudang sering tak punya tarif
 * gabungan (ongkir ditetapkan admin) — kartu memperingatkannya.
 */
export function expeditionsCommonToAll(
  expeditions: readonly Expedition[],
  warehouses: ReadonlyArray<{ expeditionIds: readonly string[] | null }>
): Expedition[] {
  return expeditions.filter(
    (e) =>
      e.isActive &&
      warehouses.every((w) => w.expeditionIds === null || w.expeditionIds.includes(e.id))
  );
}

/**
 * Nilai saklar "Ikut Perhitungan Stok" yang masih harus dikirim SESUDAH
 * `saveWarehouse` (mig 7440: simpan gudang tak lagi menulis saklar itu —
 * penulisnya `admin_set_warehouse_counts_stock`, Super Admin). `null` = tak
 * ada yang perlu dikirim: Gudang Utama (selalu dihitung), nilai tak berubah,
 * atau cabang baru yang tetap dihitung (cabang baru lahir `true`).
 */
export function countsStockUpdate(
  draft: Pick<WarehouseDraft, "id" | "isMain" | "countsStock">,
  warehouses: readonly AdminWarehouse[]
): boolean | null {
  if (draft.isMain) return null;
  if (draft.id === null) return draft.countsStock ? null : false;
  const sebelum = warehouses.find((w) => w.id === draft.id)?.countsStock ?? true;
  return draft.countsStock === sebelum ? null : draft.countsStock;
}

/** Satu pilihan "Gudang" (form produk, Daftar Produk, Pindahkan ke Gudang). */
export type WarehouseOption = {
  /** "" = Gudang Utama (`warehouse_id` NULL), selain itu id cabang. */
  value: string;
  /** Nama gudang apa adanya — dipakai juga di toast & kalimat lain. */
  label: string;
  /**
   * Saklar "Ikut Perhitungan Stok" gudang ini (mig 7350). Gudang Utama selalu
   * `true`. Baru BERLAKU selama saklar Multigudang menyala
   * (`WarehouseChoice.enabled`) — aturannya di SQL `stock_excluded_products`.
   */
  countsStock: boolean;
};

/** Pilihan "Gudang" form produk: utama (nilai "") lalu cabang aktif. */
export function warehouseSelectOptions(
  warehouses: readonly AdminWarehouse[]
): WarehouseOption[] {
  const out: WarehouseOption[] = [];
  for (const w of warehouses) {
    if (w.archivedAt !== null) continue;
    if (w.isMain) out.unshift({ value: "", label: w.name, countsStock: true });
    else if (w.id) out.push({ value: w.id, label: w.name, countsStock: w.countsStock });
  }
  if (!out.some((o) => o.value === "")) {
    out.unshift({ value: "", label: DEFAULT_MAIN_WAREHOUSE_NAME, countsStock: true });
  }
  return out;
}

/**
 * Nilai `products.warehouse_id` yang dikirim form produk. `undefined` = JANGAN
 * kirim kuncinya (pilihan gudang tak dimuat / nilainya tak dikenal — menulis
 * tebakan bisa memindahkan produk cabang ke Gudang Utama diam-diam); `null` =
 * Gudang Utama; selain itu id cabang.
 */
export function warehousePayloadValue(
  value: string | null | undefined,
  options: ReadonlyArray<{ value: string }> | null
): string | null | undefined {
  if (!options || options.length === 0 || typeof value !== "string") return undefined;
  const v = value.trim();
  if (!options.some((o) => o.value === v)) return undefined;
  return v === "" ? null : v;
}

/** Pilihan Gudang untuk form produk & Daftar Produk, plus posisi saklar. */
export type WarehouseChoice = {
  options: WarehouseOption[];
  /** Saklar Multigudang menyala — `false` = penempatan disiapkan, belum berlaku. */
  enabled: boolean;
};

/**
 * Pilihan Gudang ditampilkan begitu toko punya ≥1 gudang cabang, WALAU saklar
 * Multigudang masih mati (putusan pemilik 2026-10-14: produk boleh ditata
 * dulu). Aman karena selama saklar mati semua jalur ongkir & tampilan pembeli
 * mengabaikan `products.warehouse_id` (`shipment_warehouse_parcels`, route
 * tarif, katalog, `useWarehouseDirectory` — semuanya digerbang saklar).
 * Hanya Gudang Utama = tak ada yang dipilih → `null` (layar seperti sebelum
 * Multigudang).
 */
export function warehouseChoiceFor(
  warehouses: readonly AdminWarehouse[],
  enabled: boolean
): WarehouseChoice | null {
  const options = warehouseSelectOptions(warehouses);
  return options.length > 1 ? { options, enabled } : null;
}

/**
 * Gudang `value` SEDANG tidak ikut perhitungan stok? Hanya selama saklar
 * Multigudang menyala — saat mati semua gudang dihitung (cermin SQL
 * `stock_excluded_products`), jadi label "tidak dihitung" akan berbohong.
 * Gudang yang tak ada di pilihan (diarsip, id asing) = `false`: jangan menebak.
 */
export function warehouseIsNotCounted(
  choice: WarehouseChoice | null,
  value: string | null | undefined
): boolean {
  if (!choice?.enabled) return false;
  const opt = choice.options.find((o) => o.value === (value ?? ""));
  return opt ? !opt.countsStock : false;
}

/** Penanda pendek di belakang nama gudang yang tidak ikut perhitungan stok. */
export const NOT_COUNTED_WAREHOUSE_SUFFIX = "tidak dihitung di stok";

/**
 * Layar yang angka stoknya ditinggalkan gudang tak dihitung — kalimat yang SAMA
 * di setiap peringatan, supaya admin tak mengira tiap layar punya aturannya
 * sendiri (cermin keterangan saklar di kartu Multigudang).
 */
const STOCK_COUNT_SURFACES = "Total Nilai Aset Stok, Total Pcs, Laporan Stok, dan Persediaan Neraca";

/**
 * Teks satu `<option>` Gudang: nama, ditambah "(tidak dihitung di stok)" bila
 * gudangnya sedang tidak ikut perhitungan — admin yang memilih gudang tujuan
 * perlu tahu SEBELUM memilih, bukan sesudah Nilai Aset-nya bergeser.
 */
export function warehouseOptionText(choice: WarehouseChoice, option: WarehouseOption): string {
  return warehouseIsNotCounted(choice, option.value)
    ? `${option.label} (${NOT_COUNTED_WAREHOUSE_SUFFIX})`
    : option.label;
}

/**
 * Catatan dampak memilih gudang `value` yang diatur TIDAK ikut perhitungan
 * stok, untuk form produk (`"form"`, keadaan produk ini) dan lembar Pindahkan
 * ke Gudang (`"move"`, perubahan). `null` = gudangnya dihitung / tak dikenal.
 * Saklar Multigudang mati → tetap disebut, sebagai yang BERLAKU begitu saklar
 * dinyalakan: penempatan yang disiapkan sekarang ikut menggeser Neraca nanti.
 */
export function warehouseCountsNote(
  choice: WarehouseChoice | null,
  value: string | null | undefined,
  mode: "form" | "move"
): string | null {
  if (!choice) return null;
  const opt = choice.options.find((o) => o.value === (value ?? ""));
  if (!opt || opt.countsStock) return null;
  const dampak = STOCK_COUNT_SURFACES;
  if (!choice.enabled) {
    return `Gudang ini diatur tidak ikut perhitungan stok — begitu saklar Multigudang dinyalakan, stok ${
      mode === "move" ? "produk yang dipindahkan ke sini" : "produk ini"
    } keluar dari ${dampak}.`;
  }
  return mode === "move"
    ? `Gudang tujuan tidak ikut perhitungan stok — stok produk yang dipindahkan ke sini keluar dari ${dampak}. Produknya tetap dijual & dikirim seperti biasa.`
    : `Gudang ini tidak ikut perhitungan stok — stok produk ini tidak masuk ${dampak}. Produknya tetap dijual & dikirim seperti biasa.`;
}

/**
 * Konfirmasi sebelum menyalakan/mematikan saklar Multigudang bila ada gudang
 * cabang ber-saklar "Ikut Perhitungan Stok" mati yang berisi produk: saklar
 * Multigudang ikut menentukan apakah stok itu masuk Nilai Aset & Neraca
 * (aturan SQL `stock_excluded_products`). `null` = tak ada yang bergeser.
 */
export function multiWarehouseToggleWarning(
  warehouses: readonly AdminWarehouse[],
  next: boolean
): string | null {
  const gudang = warehouses.filter(
    (w) => !w.isMain && w.archivedAt === null && !w.countsStock && w.productCount > 0
  );
  if (gudang.length === 0) return null;
  const produk = gudang.reduce((n, w) => n + w.productCount, 0).toLocaleString("id-ID");
  const nama = joinIndonesianList(gudang.map((w) => w.name));
  // Nama gudang dalam kurung, bukan "di gudang <nama>": nama cabang sering
  // sudah berawalan "Gudang", dan "di gudang Gudang A dan Gudang B" janggal.
  const subjek = `${produk} produk di gudang yang tidak ikut perhitungan stok (${nama})`;
  const dampak = STOCK_COUNT_SURFACES;
  return next
    ? `Menyalakan Multigudang mengeluarkan stok ${subjek} dari ${dampak}. Lanjutkan?`
    : `Mematikan Multigudang mengembalikan stok ${subjek} ke ${dampak}. Ongkir semua produk juga kembali dihitung dari lokasi toko. Lanjutkan?`;
}

// ─── 7b. Saringan Gudang (Daftar Produk & /admin/stok) ──────────────────────
//
// Nilai saringan SENGAJA berbeda dari nilai pilihan form produk: di form, ""
// berarti Gudang Utama (`warehouse_id` NULL); di saringan, "" berarti SEMUA
// gudang (bawaan) dan Gudang Utama punya nilai sendiri. Nilainya cermin
// parameter `p_warehouse` RPC `admin_stock_product_order_v2` (mig 7450).

/** Saringan: hanya produk Gudang Utama (`products.warehouse_id IS NULL`). */
export const WAREHOUSE_FILTER_MAIN = "utama";
/** Saringan /admin/stok: semua produk yang TIDAK ikut perhitungan stok. */
export const WAREHOUSE_FILTER_NOT_COUNTED = "tidak_dihitung";

export type WarehouseFilterOption = { value: string; label: string };

/**
 * Pilihan saringan Gudang. `null` = toko tanpa gudang cabang (sama dengan
 * `warehouseChoiceFor`) → tanpa saringan. "Semua gudang" (nilai "") selalu
 * PERTAMA = bawaan. `withNotCounted` (khusus /admin/stok) menambah "Semua yang
 * tidak dihitung" — hanya selama Multigudang ON dan memang ada cabang ber-saklar
 * "Ikut Perhitungan Stok" mati; selain itu pilihan itu selalu kosong.
 */
export function warehouseFilterOptions(
  choice: WarehouseChoice | null,
  opts?: { withNotCounted?: boolean }
): WarehouseFilterOption[] | null {
  if (!choice) return null;
  const out: WarehouseFilterOption[] = [{ value: "", label: "Semua gudang" }];
  for (const o of choice.options) {
    out.push({
      value: o.value === "" ? WAREHOUSE_FILTER_MAIN : o.value,
      label: warehouseOptionText(choice, o),
    });
  }
  if (opts?.withNotCounted && choice.enabled && choice.options.some((o) => !o.countsStock)) {
    out.push({ value: WAREHOUSE_FILTER_NOT_COUNTED, label: "Semua yang tidak dihitung di stok" });
  }
  return out;
}

/**
 * Baca nilai saringan Gudang dari luar (query string route). `null` = semua
 * gudang; `undefined` = nilai TAK DIKENAL — pemanggil wajib menolaknya, bukan
 * diam-diam mengekspor semua gudang. `allowNotCounted` hanya untuk jalur yang
 * memang mengenal "tidak dihitung" (RPC stok v2).
 */
export function parseWarehouseFilter(
  raw: string | null | undefined,
  opts?: { allowNotCounted?: boolean }
): string | null | undefined {
  const v = (raw ?? "").trim();
  if (v === "") return null;
  if (v === WAREHOUSE_FILTER_MAIN) return v;
  if (v === WAREHOUSE_FILTER_NOT_COUNTED) return opts?.allowNotCounted ? v : undefined;
  return UUID_RE.test(v) ? v.toLowerCase() : undefined;
}

/**
 * Produk ber-`warehouseId` ini lolos saringan `filter`? Untuk menyamakan daftar
 * yang sudah termuat sesudah Pindahkan ke Gudang (tanpa fetch ulang). "Tidak
 * dihitung" tak bisa diputuskan dari id gudang saja → `null` (muat ulang).
 */
export function warehouseFilterMatches(
  filter: string | null,
  warehouseId: string | null
): boolean | null {
  if (!filter) return true;
  if (filter === WAREHOUSE_FILTER_MAIN) return warehouseId === null;
  if (filter === WAREHOUSE_FILTER_NOT_COUNTED) return null;
  return warehouseId === filter;
}

// ─── 8. Tampilan kiriman yang sudah dibuat ──────────────────────────────────
//
// "Dikirim dari …" & pengelompokan isi paket diturunkan dari gudang produk
// SAAT INI (kiriman tak menyimpan gudangnya) — produk yang dipindah sesudah
// kiriman dibuat ikut berpindah di tampilan. Nominal ongkirnya tidak.

/**
 * Nama gudang asal sekumpulan barang, urut direktori (utama dulu). Id yang
 * tak dikenal DILEWATI — kalimat "Dikirim dari" tak boleh menebak.
 */
export function originNamesForWarehouseIds(
  dir: WarehouseDirectory,
  warehouseIds: ReadonlyArray<string | null | undefined>
): string[] {
  const keys = new Set<string>();
  for (const id of warehouseIds) {
    const w = findWarehouse(dir, id);
    if (w) keys.add(w.key ?? "");
  }
  return listWarehouses(dir)
    .filter((w) => keys.has(w.key ?? ""))
    .map((w) => w.name);
}

/**
 * Nama gudang sekumpulan PRODUK lewat peta produk → gudang yang dimuat
 * terpisah (kartu Keranjang reseller di papan admin). Produk yang tak ada di
 * peta DILEWATI: `findWarehouse(dir, undefined)` akan menebaknya Gudang Utama,
 * padahal gudangnya memang belum diketahui.
 */
export function warehouseNamesForProducts(
  dir: WarehouseDirectory,
  productIds: readonly string[],
  warehouseByProduct: ReadonlyMap<string, string | null>
): string[] {
  const ids: Array<string | null> = [];
  for (const pid of productIds) {
    if (warehouseByProduct.has(pid)) ids.push(warehouseByProduct.get(pid) ?? null);
  }
  return originNamesForWarehouseIds(dir, ids);
}

/** Satu kelompok isi paket per gudang. */
export type WarehouseItemGroup<T> = {
  /** `null` = Gudang Utama; id tak dikenal tetap dengan id-nya. */
  key: string | null;
  name: string;
  address: string;
  items: T[];
};

/**
 * Isi paket → kelompok per gudang, urut direktori; gudang yang tak dikenal di
 * akhir sebagai "Gudang lain" (barang tak pernah hilang dari daftar). Urutan
 * barang di dalam kelompok = urutan asal.
 */
export function groupItemsByWarehouse<T>(
  dir: WarehouseDirectory,
  items: readonly T[],
  warehouseIdOf: (item: T) => string | null | undefined
): WarehouseItemGroup<T>[] {
  const known = new Map<string | null, WarehouseItemGroup<T>>();
  const unknown: T[] = [];
  for (const item of items) {
    const w = findWarehouse(dir, warehouseIdOf(item));
    if (!w) {
      unknown.push(item);
      continue;
    }
    const g = known.get(w.key) ?? { key: w.key, name: w.name, address: w.address, items: [] };
    g.items.push(item);
    known.set(w.key, g);
  }
  const out = listWarehouses(dir)
    .map((w) => known.get(w.key))
    .filter((g): g is WarehouseItemGroup<T> => g !== undefined);
  if (unknown.length > 0) {
    out.push({ key: "?", name: "Gudang lain", address: "", items: unknown });
  }
  return out;
}

/**
 * Rincian ongkir per gudang untuk admin, dari kutipan yang memang menagih
 * kiriman ini. Ditampilkan HANYA bila totalnya masih sama dengan ongkir
 * kiriman — ongkir yang sudah diubah manual tak boleh disandingkan dengan
 * rincian yang tak lagi menjumlah ke angkanya.
 */
export function quoteBreakdownFor(
  quote: { amount: number; legs: unknown } | null | undefined,
  shipmentOngkir: number
): RateLeg[] {
  if (!quote) return [];
  const legs = parseQuoteLegs(quote.legs);
  if (legs.length < 2) return [];
  const sum = legs.reduce((s, l) => s + l.price, 0);
  if (Math.round(quote.amount) !== Math.round(shipmentOngkir) || sum !== Math.round(quote.amount)) {
    return [];
  }
  return legs;
}

/**
 * Daftar admin (RPC, lengkap dengan alamat) → direktori. Dipakai layar admin
 * yang mencetak alamat gudang (surat jalan); pembaca nama saja memakai
 * `fetchWarehouseDirectory`.
 */
export function directoryFromAdminWarehouses(list: readonly AdminWarehouse[]): WarehouseDirectory {
  const toView = (w: AdminWarehouse): WarehouseView => ({
    key: w.isMain ? null : w.id,
    id: w.id,
    name: w.name,
    isMain: w.isMain,
    areaId: w.isMain ? null : w.areaId,
    districtKey: w.isMain ? null : w.districtKey,
    locationLabel: w.locationLabel,
    address: w.address,
    countsStock: w.countsStock,
    expeditionIds: w.expeditionIds,
  });
  const main = list.find((w) => w.isMain);
  return {
    main: main ? toView(main) : defaultMainWarehouse(),
    branches: list.filter((w) => !w.isMain && w.archivedAt === null && w.id !== null).map(toView),
  };
}

/**
 * Gudang satu barang untuk dokumen cetak (`ShippingLabelItem.warehouse`).
 * Id tak dikenal → "Gudang lain" di urutan terakhir — barang tak pernah
 * hilang dari surat jalan karena gudangnya tak terbaca.
 */
export function labelWarehouseFor(
  dir: WarehouseDirectory,
  warehouseId: string | null | undefined
): { key: string; name: string; address: string; order: number } {
  const list = listWarehouses(dir);
  const w = findWarehouse(dir, warehouseId);
  if (!w) return { key: "?", name: "Gudang lain", address: "", order: list.length };
  return {
    key: w.key ?? "",
    name: w.name,
    address: w.address,
    order: Math.max(0, list.findIndex((x) => x.key === w.key)),
  };
}
